/**
 * @fileoverview Servicio HTTP del Centro de Ayuda.
 * Consume los endpoints /v1/help-center/* de portal-api-zonasegura publicados en Apigee (proxy api-ux-sz-customers):
 * catálogo, búsqueda y registro del tracking de uso. Usa el axios global, así que los interceptores de main.js
 * agregan el access token, `x-api-key`, las cabeceras de trazabilidad y la renovación de sesión (ver
 * `environment.getZsBackendUrls`). El usuario del tracking lo toma el backend del token: no viaja en el body.
 * La base (API_ZONASEGURA) se lee desde utils/environment.
 * @module services/help-center.service
 */

import axios from 'axios'
import environment from '@/utils/environment'

/**
 * URL absoluta de un recurso del centro de ayuda.
 * @param {string} resource - Ruta bajo `help-center/`
 * @returns {string} URL completa; la base ya termina en '/'
 */
function endpoint (resource) {
  return `${environment.getPortalApiUrl()}help-center/${resource}`
}

/**
 * Códigos de producto sin repetir ni vacíos. La página arma uno por póliza, así que un usuario con varias pólizas
 * del mismo producto envía el código repetido; al backend le basta uno.
 * @param {Array<string>} plans - Códigos de los productos del usuario
 * @returns {Array<string>} Códigos únicos, en el orden en que aparecen
 */
function uniquePlans (plans) {
  return Array.from(new Set((plans || []).filter(Boolean)))
}

/**
 * Obtiene el catálogo (productos con sus temas y preguntas) de los productos del usuario.
 * Sin productos no hay nada que pedir: el backend exige al menos uno.
 * @param {Array<string>} plans - Códigos de los productos del usuario
 * @returns {Promise<Array<Object>>} Productos con `topics` y `questions`
 */
export function fetchCatalog (plans) {
  const codes = uniquePlans(plans)
  if (!codes.length) {
    return Promise.resolve([])
  }
  return axios.get(endpoint('products'), { params: { plans: codes.join(',') } })
    .then(res => (res.data && res.data.products) || [])
}

/**
 * Busca preguntas por palabra clave dentro de los productos del usuario. Un término vacío o de solo espacios
 * (por ejemplo al salir de la vista de resultados) no consulta nada: no hay búsqueda que hacer.
 * @param {Array<string>} plans - Códigos de los productos del usuario
 * @param {string} search - Texto buscado
 * @returns {Promise<Array<Object>>} Preguntas que coinciden ({id, order, question, response, topicId, plans})
 */
export function searchQuestions (plans, search) {
  const codes = uniquePlans(plans)
  const term = typeof search === 'string' ? search.trim() : ''
  if (!codes.length || !term) {
    return Promise.resolve([])
  }
  return axios.post(endpoint('questions/searches'), { search: term, plans: codes })
    .then(res => (res.data && res.data.questions) || [])
}

/**
 * Arma el body del tracking. El `user` no se envía: el backend lo toma del token.
 * @param {Object} event - Evento a registrar
 * @param {Object} [event.data] - Datos del usuario (nombres, apellidos, teléfono) más el contexto del evento
 * @param {string} event.moduleName - Módulo que se registra (tema, pregunta o 'Busqueda')
 * @param {string} event.origin - Producto de origen
 * @param {string} event.action - Acción (ver TRACKING_ACTIONS)
 * @returns {Object} Body listo para enviar
 */
export function buildTrackingBody ({ data, moduleName, origin, action }) {
  return {
    data,
    module: moduleName,
    page: window.location.pathname,
    device: navigator.userAgent,
    origin,
    action
  }
}

/**
 * Registra un evento de uso. Nunca rechaza: un fallo de tracking no debe afectar la pantalla.
 * No se loguea el payload porque lleva nombres y teléfono del usuario.
 * @param {Object} event - Evento a registrar (ver buildTrackingBody)
 * @returns {Promise<*>} Promesa que siempre resuelve
 */
export function trackEvent (event) {
  return axios.post(endpoint('tracking-events'), buildTrackingBody(event)).catch(() => {})
}



// WEBPACK FOOTER //
// ./src/services/help-center.service.js